import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookmarkSimple, Plus, X, Trash } from "@phosphor-icons/react";
import { collectionsApi, postsApi } from "@/lib/apiClient";
import PostCard from "./PostCard";
import CommentsDrawer from "./CommentsDrawer";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function SavedCollections() {
    const { t } = useTranslation();
    const [collections, setCollections] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [posts, setPosts] = useState([]);
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState("");
    const [commentPost, setCommentPost] = useState(null);

    const loadCollections = async () => {
        try {
            const list = await collectionsApi.list();
            setCollections(list);
            if (list.length && !activeId) setActiveId(list[0].id);
        } catch (e) {}
    };
    const loadPosts = async (id) => {
        try {
            setPosts(await collectionsApi.posts(id));
        } catch { setPosts([]); }
    };

    useEffect(() => { loadCollections(); }, []);
    useEffect(() => { if (activeId) loadPosts(activeId); }, [activeId]);

    const create = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        try {
            const c = await collectionsApi.create({ name: name.trim() });
            setCollections((prev) => [...prev, c]);
            setActiveId(c.id);
            setName(""); setCreating(false);
            toast.success(t("savedCollections.toasts.created"));
        } catch { toast.error(t("savedCollections.toasts.createFailed")); }
    };

    const remove = async (id) => {
        if (!window.confirm(t("savedCollections.confirmDelete"))) return;
        try {
            await collectionsApi.remove(id);
            const next = collections.filter((c) => c.id !== id);
            setCollections(next);
            if (activeId === id) setActiveId(next[0]?.id || null);
            toast.success(t("savedCollections.toasts.deleted"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("savedCollections.toasts.cannotDelete"));
        }
    };

    return (
        <div data-testid="saved-collections">
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <div>
                    <div className="overline mb-1">{t("savedCollections.overline")}</div>
                    <div className="font-serif text-3xl text-white leading-none">{t("savedCollections.title")}</div>
                </div>
                <button onClick={() => setCreating(true)} className="btn-gold text-sm !py-2.5 !px-5" data-testid="collections-new">
                    <Plus size={12} weight="bold" /> {t("savedCollections.newCollection")}
                </button>
            </div>

            <div className="flex gap-2 flex-wrap mb-6">
                {collections.map((c) => (
                    <button key={c.id} onClick={() => setActiveId(c.id)} className="text-xs px-3 py-1.5 rounded-full flex items-center gap-2" style={{ background: activeId === c.id ? "var(--gold)" : "rgba(0,0,0,0.3)", color: activeId === c.id ? "var(--navy)" : "white", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`collection-tab-${c.id}`}>
                        <BookmarkSimple size={12} weight={activeId === c.id ? "fill" : "regular"} />
                        {c.name} <span className="opacity-70">· {c.count}</span>
                        {!c.is_default && activeId === c.id && (
                            <span onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="ml-1 hover:text-red-500 cursor-pointer" role="button">
                                <Trash size={11} />
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {posts.length === 0 ? (
                <div className="text-center py-16 rounded-2xl" style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                    <div className="text-white/60">{t("savedCollections.empty")}</div>
                </div>
            ) : (
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [&>*]:mb-6">
                    {posts.map((p) => (
                        <PostCard key={p.id} post={p} onOpenComments={setCommentPost} onUpdate={(updates) => setPosts((prev) => prev.map((post) => post.id === p.id ? { ...post, ...updates } : post))} />
                    ))}
                </div>
            )}

            <AnimatePresence>
                {creating && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(5,10,17,0.85)", backdropFilter: "blur(8px)" }} onClick={() => setCreating(false)}>
                        <motion.form onClick={(e) => e.stopPropagation()} onSubmit={create} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="rounded-3xl max-w-sm w-full p-6" style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)" }}>
                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <div className="overline mb-1">{t("savedCollections.modal.overline")}</div>
                                    <div className="font-serif text-xl text-white">{t("savedCollections.modal.title")}</div>
                                </div>
                                <button type="button" onClick={() => setCreating(false)} className="w-8 h-8 rounded-full glass grid place-items-center"><X size={12} /></button>
                            </div>
                            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("savedCollections.modal.placeholder")} autoFocus className="w-full bg-black/30 border rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[color:var(--gold)]" style={{ borderColor: "rgba(255,255,255,0.1)" }} data-testid="collections-name" />
                            <button type="submit" className="btn-gold w-full justify-center mt-4 text-sm" data-testid="collections-create">{t("savedCollections.modal.create")}</button>
                        </motion.form>
                    </motion.div>
                )}
            </AnimatePresence>

            <CommentsDrawer post={commentPost} open={!!commentPost} onClose={() => setCommentPost(null)} />
        </div>
    );
}
